/**
 * Turning something written into something worth hearing.
 *
 * An answer from the assistant is Markdown: headings, bold, links, and very
 * often a table — because that is how a language model lays out seven customers
 * and what each of them owes. Handed to a speech engine as-is it reads the
 * asterisks, spells the pipe characters, and recites a twenty-four character
 * database id one hexadecimal digit at a time.
 *
 * So this is not a Markdown stripper. It decides what is worth saying out loud
 * and what is only worth showing:
 *
 *   · a table is named, not read — nobody listens to seven rows of figures and
 *     remembers the third one, and the table is right there on the screen;
 *   · an id is dropped entirely, because it is written for a machine;
 *   · a link keeps its words and loses its path;
 *   · and the whole thing is capped, because an answer that takes two minutes
 *     to hear is an answer nobody hears the end of.
 *
 * The phrases it needs are passed in rather than written here: the ui-kit does
 * not own anybody's translations.
 */

/** Roughly how many characters of speech fit in a comfortable listen (~45 s). */
const DEFAULT_MAX_CHARS = 700

/**
 * A database id, and nothing else.
 *
 * Sixteen hex characters or more, which no word reaches and no figure a person
 * would say out loud. Deliberately not matching shorter runs: `2026` is a year
 * and `0104` is an invoice number, and both belong in the sentence.
 */
const MACHINE_ID = /\b[0-9a-f]{16,}\b/gi

const FENCED_CODE = /```[\s\S]*?```/g
const TABLE_BLOCK = /(?:^[ \t]*\|.*\|[ \t]*$\n?)+/gm
/** A table's own separator row: |---|:--:|. Never a row of data. */
const TABLE_RULE = /^[\s|:-]+$/

const DEFAULT_PHRASES = {
  /** @param {number} rows */
  table: (rows) => `A table of ${rows} rows, on screen.`,
  code: () => 'There is a block of code on screen.',
  more: () => 'It goes on, on screen.',
}

/**
 * @param {string} markdown       what the assistant wrote
 * @param {object} [options]
 * @param {number} [options.maxChars]
 * @param {object} [options.phrases]  table(rows), code(), more() — translated
 * @returns {{ text: string, truncated: boolean, hadTable: boolean }}
 */
export function speakable(markdown, { maxChars = DEFAULT_MAX_CHARS, phrases } = {}) {
  const say = { ...DEFAULT_PHRASES, ...(phrases ?? {}) }
  let hadTable = false

  let text = String(markdown ?? '')

  // ── Whole blocks, before anything touches their insides ───────────────────
  text = text.replace(FENCED_CODE, () => `\n${say.code()}\n`)

  text = text.replace(TABLE_BLOCK, (block) => {
    // The header and the |---| rule are not rows of anything. Counting them
    // would tell somebody there are nine customers when there are seven.
    const lines = block.trim().split('\n')
    const rows = lines.filter(line => !TABLE_RULE.test(line)).length - 1
    if (rows < 1) return '\n'
    hadTable = true
    return `\n${say.table(rows)}\n`
  })

  // ── Inline syntax ─────────────────────────────────────────────────────────
  text = text
    // A link is its words. The path is for the eye and the finger.
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    // Headings become sentences: the full stop is what makes the voice pause.
    .replace(/^#{1,6}\s*(.+?)\s*#*$/gm, (_, heading) => `${heading.replace(/[.:]$/, '')}.`)
    .replace(/^\s*>\s?/gm, '')
    // Bullets and numbering: the pause between lines already says "next one".
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    // Emphasis and inline code carry nothing a voice can pronounce.
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(?<!\w)[*_]([^*_\n]+)[*_](?!\w)/g, '$1')
    .replace(/`([^`\n]+)`/g, '$1')
    // A horizontal rule is a page-design device.
    .replace(/^\s*([-*_])\1{2,}\s*$/gm, '')
    .replace(MACHINE_ID, '')

  // ── Rhythm ────────────────────────────────────────────────────────────────
  // A line that ends without punctuation runs into the next one, and the whole
  // answer comes out as one breathless sentence.
  text = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => (/[.!?:;…]$/.test(line) ? line : `${line}.`))
    .join(' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim()

  const truncated = text.length > maxChars
  if (truncated) text = `${cutAtSentence(text, maxChars)} ${say.more()}`

  return { text, truncated, hadTable }
}

/**
 * Cut where a sentence ends, or failing that where a word does.
 *
 * Cutting mid-word is audible in a way it is not visible: a voice that stops on
 * "Fusteri" sounds broken, where the same cut on screen just looks clipped.
 */
function cutAtSentence(text, maxChars) {
  const window = text.slice(0, maxChars)
  const lastStop = Math.max(window.lastIndexOf('. '), window.lastIndexOf('! '), window.lastIndexOf('? '))
  if (lastStop > maxChars * 0.5) return window.slice(0, lastStop + 1)

  const lastSpace = window.lastIndexOf(' ')
  return `${lastSpace > 0 ? window.slice(0, lastSpace) : window}…`
}

export { DEFAULT_MAX_CHARS }
